<template>
  <b-modal
    :id="modalId"
    title="Transfer to Other Ticket"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    centered
    @show="resetTransferModal"
    @hidden="resetTransferModal"
  >
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="container-fluid px-3 d-flex">
        <div class="flex-grow-1">
          <div class="btn-group mr-2">
            <button
              type="button"
              class="btn btn-itg-dark"
              :disabled="isTransferring"
            >
              <i class="fa fa-search mr-2"></i>
              Search Ticket
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Current Ticket Info Bar -->
    <div class="ticket-info-bar">
      <div class="container-fluid px-3 d-flex align-items-center">
        <div class="info-item">
          <i class="fas fa-ticket-alt"></i>
          <span class="info-label">Current Ticket:</span>
          <span class="info-value">#{{ ticket.id }}</span>
        </div>
        <div v-if="ticket.axum_booking_id" class="info-item ml-4">
          <i class="fas fa-plane"></i>
          <span class="info-label">Axum Booking:</span>
          <span class="info-value">{{ ticket.axum_booking_id }}</span>
        </div>
      </div>
    </div>

    <!-- Modal Body -->
    <div
      class="transfer-ticket-form px-4 pt-4 pb-0 w-100"
      style="border-radius: 0"
    >
      <h5 class="mb-3 font-weight-bold">Search for Ticket to Transfer To</h5>
      <div class="form-group">
        <label for="search-query">Search Tickets</label>
        <input
          :id="`search-query-${modalId}`"
          v-model="ticketSearchQuery"
          type="text"
          class="form-control"
          placeholder="Enter ticket ID, subject, or description to search..."
        />
      </div>

      <div v-if="isSearchingTickets" class="text-center py-4">
        <b-spinner></b-spinner>
        <p class="mt-2">Searching...</p>
      </div>

      <div
        v-else-if="searchResults && searchResults.length > 0"
        class="search-results"
      >
        <h6 class="mb-3">Search Results</h6>
        <div class="ticket-list">
          <selectable-ticket-card
            v-for="ticket in searchResults"
            :key="ticket.id"
            :ticket="ticket"
            :selected="selectedTicket && selectedTicket.id === ticket.id"
            @select="selectTicket"
            class="mb-2"
          />
        </div>
      </div>

      <div
        v-else-if="ticketSearchQuery && !isSearchingTickets"
        class="text-center py-4"
      >
        <p class="text-muted">No tickets found matching your search.</p>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end align-items-center">
        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-itg"
            @click="$bvModal.hide(modalId)"
            :disabled="isTransferring"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn-itg-dark"
            @click="transferEntry"
            :disabled="!selectedTicket || isTransferring"
          >
            <span v-if="isTransferring">
              <i class="fa fa-spinner fa-spin mr-1"></i>
              Transferring...
            </span>
            <span v-else>Transfer Entry</span>
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import _ from "lodash";
import SelectableTicketCard from "../TicketToolbar/SelectableTicketCard.vue";

export default {
  name: "TransferModal",
  components: { SelectableTicketCard },
  props: {
    modalId: {
      type: String,
      required: true,
    },
    entryType: {
      type: String,
      required: true,
      validator: (value) => {
        return ["inbound_whatsapp_message", "ticket_whatsapp_log", "ticket_note"].includes(value);
      },
    },
    entryId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      ticketSearchQuery: "",
      searchResults: [],
      selectedTicket: null,
      isSearchingTickets: false,
      isTransferring: false,
    };
  },
  computed: {
    ...mapGetters("ticketViewer", {
      ticket: "ticket",
    }),
  },
  watch: {
    ticketSearchQuery() {
      this.searchTickets();
    },
  },
  methods: {
    resetTransferModal() {
      this.ticketSearchQuery = "";
      this.searchResults = [];
      this.selectedTicket = null;
      this.isSearchingTickets = false;
      this.isTransferring = false;
    },

    searchTickets: _.debounce(function () {
      if (!this.ticketSearchQuery.trim()) {
        this.searchResults = [];
        return;
      }

      this.isSearchingTickets = true;

      axios
        .post(API_BASE + "/tickets/search", {
          q: this.ticketSearchQuery,
          results_per_page: 10,
        })
        .then((response) => {
          this.isSearchingTickets = false;
          // Filter out the current ticket from results on frontend
          this.searchResults = (response.data.data || []).filter(
            (ticket) => ticket.id !== this.ticket.id
          );
        })
        .catch((error) => {
          this.isSearchingTickets = false;
          console.error("Error searching tickets:", error);
          this.$notify.error({
            title: "Error",
            message: "Failed to search tickets",
          });
        });
    }, 500),

    selectTicket(ticket) {
      this.selectedTicket = ticket;
    },

    async transferEntry() {
      if (!this.selectedTicket) {
        this.$notify.error({
          title: "Error",
          message: "Please select a ticket to transfer to.",
        });
        return;
      }

      this.isTransferring = true;

      try {
        const response = await axios.post(API_BASE + "/tickets/move-entry", {
          type: this.entryType,
          movable_id: this.entryId,
          old_ticket_id: this.ticket.id,
          new_ticket_id: this.selectedTicket.id,
        });

        // Success notification
        this.$notify.success({
          title: "Success!",
          message: response.data.message || "Entry moved successfully",
        });

        // Close modal
        this.$bvModal.hide(this.modalId);

        // Emit event for parent to handle navigation
        this.$emit("transferred", {
          newTicketId: this.selectedTicket.id,
          response: response.data,
        });
      } catch (error) {
        console.error("Error transferring entry:", error);
        this.$notify.error({
          title: "Error",
          message: error.response?.data?.message || "Failed to transfer entry",
        });
      } finally {
        this.isTransferring = false;
      }
    },
  },
};
</script>

<style lang="scss">
[id^="transfer-modal"].modal.show .modal-dialog {
  max-width: 100vw !important;
  width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
  transform: none !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
}

[id^="transfer-modal"].modal.show .modal-dialog .modal-body {
  padding: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  max-height: calc(100vh - 127px) !important;
}

[id^="transfer-modal"].modal.show .modal-dialog .modal-header {
  border-bottom: none !important;
  background: #2a3042 !important;
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
  padding-left: 1.2rem !important;
  padding-right: 1.2rem !important;
}

[id^="transfer-modal"].modal.show .modal-dialog .modal-header .modal-title {
  color: #fff !important;
}

[id^="transfer-modal"].modal.show .modal-dialog .modal-header .close {
  filter: invert(1) !important;
  padding-top: 18px !important;
  padding-right: 20px !important;
}

[id^="transfer-modal"].modal.show .modal-dialog .modal-content {
  border: 0 !important;
  min-height: calc(100vh - 300px) !important;
  height: 100vh !important;
  max-height: 100vh !important;
}

.transfer-ticket-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  flex: 1;

  .form-group {
    label {
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.5rem;
    }

    .form-control {
      border-radius: 0.5rem;
      border: 1px solid #ced4da;
      padding: 0.75rem 1rem;
      height: auto;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

      &:focus {
        border-color: #2a3042;
        box-shadow: 0 0 0 0.2rem rgba(42, 48, 66, 0.25);
      }
    }
  }
}

.toolbar {
  border-top: 1px #ebeff3 solid !important;
  border-bottom: 1px #cfd7df solid !important;
  padding-top: 11px;
  padding-bottom: 11px;
  box-shadow: 0 2px 4px 0 rgba(24, 50, 71, 0.08);
  z-index: 1;

  .btn-group {
    .btn {
      border-radius: 0.375rem;
      margin-right: 0.5rem;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.ticket-info-bar {
  background: #fff;
  padding: 11px 0;
  border-bottom: 1px solid #cfd7df;
  box-shadow: 0 2px 4px 0 rgba(24, 50, 71, 0.08);

  .info-item {
    display: flex;
    align-items: center;
    color: #495057;

    i {
      color: #74788d;
      margin-right: 8px;
    }

    .info-label {
      color: #74788d;
      font-size: 0.875rem;
      margin-right: 6px;
    }

    .info-value {
      font-weight: 600;
      font-size: 0.875rem;
      color: #495057;
    }
  }
}

:deep(.modal-footer) {
  border-top: 1px solid #dee2e6;
  padding-left: 1rem;
  padding-right: 1rem;
}

:deep(.modal-footer .btn) {
  min-width: 100px;
}

.ticket-item {
  transition: all 0.2s ease;

  &:hover {
    .card {
      box-shadow: 0 0.75rem 1.5rem rgb(18 38 63 / 15%) !important;
    }
  }

  &.selected {
    .card {
      border: 2px solid #2196f3 !important;
      box-shadow: 0 0.75rem 1.5rem rgb(33 150 243 / 20%) !important;
    }
  }
}

.gap-2 {
  gap: 0.5rem;
}
</style>
